import { PhoneNumber } from '../phone/phone';

export interface ConversationNote {
  id: string;
  note: string;
  source: string;
  user?: string;
  noteableId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Appointment {
  date: string;
  description: string;
  url: string;
  reference: 'google_calendar' | 'calendly' | string;
  attendees: string[];
  timezone: string;
}

export interface ScenarioCapture {
  label: string;
  value: string;
}

export interface Conversation {
  id: string;
  businessId: number;
  customerId: number;
  bookingId: number;
  transcript: string;
  summary: string;
  events: any[];
  type: ConversationType;
  entityType: EntityType; // Used for routing to correct endpoint (calls/{id} or chats/{id}). Also determines if conversation is SMS (Chat) or Call (Call)
  channel?: ConversationChannel;
  source?: ConversationSource; // 'phone' or 'webchat'
  customer: Customer;
  // bookings: Booking[];
  viewed: boolean;
  flagged: boolean;
  startedAt: string;
  endedAt: string;
  recordingUrl: string;
  hasRecordingExpired: boolean;
  /** Whether the recording file itself is available — recordingUrl is a static route. */
  recordingReady?: boolean;
  status: ConversationState;
  bookingMessage: boolean;
  sentLinks: any[]
  transferDestination?: {
    department: string;
    number: string;
    type: any;
    answeredPhoneNumber?: string;
    answeredLabel?: string;
    attempts?: { phoneNumber: string; label: string; priority: number; outcome: string }[];
  };
  appointment?: Appointment | null;
  questions?: any[];
  scenarioName?: string;
  scenarioCaptures?: ScenarioCapture[];
  matchedScenarioUuid?: string;
  rating?: number | null;
  ratingReason?: string | null;
  // SMS-specific fields
  turns?: ConversationTurn[]; // Individual message turns with timestamps
  // Notes
  notes?: ConversationNote[];
  // Meta channel takeover fields
  takeoverStatus?: string;
  takenOverByUserId?: string;
  takeoverAt?: string;
  channelIdentifier?: string;
  channelPageId?: string;
  messagingWindowExpiresAt?: string;
  canSendMessage?: boolean;
  customerProfile?: CustomerProfile;
  crmRecord?: CrmRecord;
  isTest?: boolean;
  /** Truncated preview of the latest message, sent with realtime list updates. */
  lastMessage?: string;
}

export interface CrmRecord {
  provider: string;
  providerLabel: string;
  contactId: string;
  contactName?: string;
  recordUrl?: string;
  loggedAt?: string;
}

export interface CustomerProfile {
  channel?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  profilePicUrl?: string;
  followerCount?: number;
  isUserFollowBusiness?: boolean;
  fetchedAt?: string;
}

// Alias for backwards compatibility
export type Call = Conversation;

export interface ConversationTurn {
  id?: string;
  role: 'agent' | 'user' | 'system';
  content: string;
  timestamp: string;
  event?: 'takeover' | 'handback' | null;
  sentByHuman?: boolean;
}

export function isMessagingWindowClosed(conversation: Conversation): boolean {
  if (!conversation?.messagingWindowExpiresAt) {
    return false;
  }

  return new Date(conversation.messagingWindowExpiresAt).getTime() < Date.now();
}

export function yelpConversationUrl(conversation: Conversation): string {
  const yelpBusinessId = conversation?.channelPageId;

  if (!yelpBusinessId) {
    return 'https://biz.yelp.com';
  }

  const yelpLeadsUrl = `https://biz.yelp.com/leads_center/${encodeURIComponent(yelpBusinessId)}/leads`;
  const yelpLeadId = conversation?.channelIdentifier;

  return yelpLeadId ? `${yelpLeadsUrl}/${encodeURIComponent(yelpLeadId)}` : yelpLeadsUrl;
}

export enum EntityType {
  Call = 'call',
  Chat = 'chat'
}

export enum ConversationSource {
  Phone = 'phone',
  Webchat = 'webchat'
}

export enum ConversationChannel {
  Call = 'call',
  Sms = 'sms',
  Web = 'web',
  Messenger = 'messenger',
  Instagram = 'instagram',
  Yelp = 'yelp'
}

export interface Customer {
  id: number;
  businessId: number;
  firstName: string;
  lastName: string;
  /** Pre-composed display name; only realtime payloads send it. */
  name?: string;
  isLead: boolean;
  additionalData: AdditionalData;
  phoneNumbers: PhoneNumber[];
  phoneNumber: string;
}

export interface AdditionalData {
  height: string;
}

// export interface Booking {
//   id: number;
//   businessId: number;
//   customerId: number;
//   type: string;
//   date: string;
//   meta: any;
// }

export enum ConversationType {
  Appointment = 'appointment',
  Transferred = 'transferred',
  UnsuccessfulTransferAttempt = 'unsuccessful_transfer_attempt',
  Message = 'message',
  Spam = 'spam',
  Blocked = 'blocked',
  HangUp = 'hangup',
}

export enum ConversationState {
  InProgress = 'IN_PROGRESS',
  Processing = 'PROCESSING',
  Finished = 'FINISHED',
  Failed = 'FAILED',
  // Chat statuses
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  AgentHandling = 'AGENT_HANDLING',
  TakeoverPending = 'TAKEOVER_PENDING',
  HumanHandling = 'HUMAN_HANDLING',
}

export function normalizeCallState(status: string): ConversationState {
  return status as ConversationState;
}

/** A conversation still in progress (live call or open chat): not finished, failed or inactive. */
export function isLiveConversationState(status: ConversationState | string | undefined): boolean {
  return !!status && status !== ConversationState.Finished && status !== ConversationState.Failed && status !== ConversationState.Inactive;
}
